import React, { useCallback, useMemo } from "react";
import { Button } from "antd";
import * as R from "ramda";

import Toggle from "../../../../../../components/inputs/Toggle";

import { isFixedOption, isFixedQuestion, questionHasError } from "./constants";

import styled from "styled-components/macro";

const ActionsContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;

  button {
    flex-shrink: 0;
    margin-left: 8px;
  }
`;

function QuestionActions({
  editQuestionsEnabled,
  expandedRows,
  isDisabled,
  getRowKey,
  record,
  removeOption,
  removeQuestionsEnabled,
  setExpandedRows,
  setFieldValue,
  setQuestionModal,
  updateOption
}) {
  const rowKey = useMemo(() => getRowKey(record), [getRowKey, record]);

  const isExpanded = useMemo(() => R.includes(rowKey, expandedRows), [
    expandedRows,
    rowKey
  ]);

  const showExpandRowButton = useMemo(
    () =>
      record.configurable &&
      (record.enabled ||
        record.interaction ||
        questionHasError(record) ||
        !(record.isEmpty && record.isEmpty(record))),
    [record]
  );

  const handleChange = useCallback(
    enabled => {
      if (isFixedOption(record)) {
        setFieldValue(`fixedOptions.${record.formValueKey}`, enabled);
      } else {
        if (
          enabled &&
          record.configurable &&
          ((!record.interaction && record.isEmpty && record.isEmpty(record)) ||
            questionHasError(record))
        ) {
          setExpandedRows(isExpanded ? R.identity : R.append(rowKey));
        }
        updateOption(record, {
          enabled,
          interaction: true
        });
      }
    },
    [isExpanded, record, rowKey, setExpandedRows, setFieldValue, updateOption]
  );

  const handleExpandRow = useCallback(
    () =>
      setExpandedRows(
        isExpanded ? R.reject(R.equals(rowKey)) : R.append(rowKey)
      ),
    [isExpanded, rowKey, setExpandedRows]
  );

  const handleEdit = useCallback(
    () =>
      setQuestionModal({
        isVisible: true,
        questionIndex: record.index,
        kind:
          record.type === "test"
            ? "evaluation"
            : record.type === "document_upload"
            ? "document_upload"
            : "question"
      }),
    [record.index, record.type, setQuestionModal]
  );

  const handleRemove = useCallback(() => removeOption(record), [
    record,
    removeOption
  ]);

  return (
    <ActionsContainer>
      {isFixedOption(record) || isFixedQuestion(record) ? (
        <>
          {showExpandRowButton && (
            <Button
              data-test={`${record.type}-${
                isExpanded ? "expanded" : "collapsed"
              }`}
              type={isExpanded ? "primary" : "default"}
              onClick={handleExpandRow}
              shape="circle-outline"
              icon="edit"
            />
          )}
          <Toggle
            checked={record.enabled}
            data-test={`screening-option-${
              isFixedOption(record) ? record.formValueKey : record.type
            }`}
            disabled={isDisabled}
            onChange={handleChange}
          >
            {({ toggle }) => toggle}
          </Toggle>
        </>
      ) : (
        <>
          <Button
            disabled={!editQuestionsEnabled}
            shape="circle-outline"
            icon="edit"
            onClick={handleEdit}
          />
          <Button
            disabled={!removeQuestionsEnabled}
            shape="circle-outline"
            icon="delete"
            onClick={handleRemove}
          />
        </>
      )}
    </ActionsContainer>
  );
}

export default QuestionActions;
